// note-aliases.ts — the vault's alias dictionary, mined from its own links.
//
// A term means a note ONLY if the user has linked that exact term text to it
// (`[matt](000001_Mathilde_Gady)`). We mine those explicit display-text links —
// ZERO statistical inference — so a place mentioned near a person ("wales") can
// never be mistaken for that person's alias. The Labyrinth thread flow uses this to
// PROPAGATE an alias the user already declared to the other places the term appears
// bare. Targets may be notes that don't exist yet but are linked to (phantom
// nodes), so a term can be linked to a not-yet-created note.

import { extractLinkDisplayTargets, type NoteRecord } from './note-graph-index.js';

export interface AliasOptions {
  /** reserved / virtual names to skip as alias targets. */
  isExcluded?: (name: string) => boolean;
}

/** Usable if ≥2 chars and not a bare note-id number. */
function isUsableTerm(term: string): boolean {
  return term.length >= 2 && !/^\d+$/.test(term);
}

/**
 * Build the alias dictionary from the vault's explicit links: lowercased TERM →
 * set of target notes the user has linked that term to. Sourced ONLY from
 * `[display](target)` / `[[wiki]]` links (no inference). A term the user has linked
 * to 2+ different notes ⇒ `Set.size > 1` ⇒ ambiguous (the caller asks which).
 * Targets are kept even when the note doesn't exist yet (referenced-but-uncreated).
 */
export function buildAliasIndex(
  notes: NoteRecord[],
  opts: AliasOptions = {}
): Map<string, Set<string>> {
  const isExcluded = opts.isExcluded ?? (() => false);
  const index = new Map<string, Set<string>>();
  for (const { display, target } of extractLinkDisplayTargets(notes)) {
    const term = display.trim().toLowerCase();
    if (!isUsableTerm(term) || isExcluded(target)) continue;
    if (/[[\]]/.test(target)) continue; // malformed target (e.g. a `[[wiki]]` used as md target)
    let set = index.get(term);
    if (!set) {
      set = new Set();
      index.set(term, set);
    }
    set.add(target);
  }
  return index;
}

/** The target note(s) a term refers to, sorted; length > 1 ⇒ ambiguous. */
export function resolveAlias(targets: Set<string> | undefined): string[] {
  return targets ? [...targets].sort() : [];
}
